import { FC, useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  LearningSessionAction,
  ResponseState,
} from 'src/apps/LearningSession/hooks/learningSessionReducer';
import { HanziWriterTracer } from './HanziWriterTracer';
import { CanvasTracer } from './CanvasTracer';
import {
  getTracingCharacterType,
  getStoredTracingDifficulty,
  STROKE_ACCURACY_THRESHOLD,
  MAX_ATTEMPTS,
} from './utils';
import * as S from './styles';

const msgs = defineMessages({
  tryAgain: {
    id: 'tracing_test_try_again',
    defaultMessage: 'Not quite - try again!',
  },
  correct: {
    id: 'tracing_test_correct',
    defaultMessage: 'Well done!',
  },
});

interface Props {
  character: string;
  responseState: ResponseState;
  learningSessionDispatch: (action: LearningSessionAction) => void;
}

export const TracingResponse: FC<Props> = ({
  character,
  responseState,
  learningSessionDispatch,
}) => {
  const intl = useIntl();
  const [attempts, setAttempts] = useState(0);
  const [feedback, setFeedback] = useState<'correct' | 'retry' | null>(null);
  const difficulty = getStoredTracingDifficulty();
  const characterType = getTracingCharacterType(character);
  const isDisabled = responseState !== 'Selected' && responseState !== 'Unanswered';

  const handleComplete = useCallback(
    (accuracy: number) => {
      if (accuracy >= STROKE_ACCURACY_THRESHOLD) {
        setFeedback('correct');
        learningSessionDispatch({
          type: 'ANSWER_TEST',
          answer: character,
        });
      } else if (attempts + 1 < MAX_ATTEMPTS) {
        setAttempts(prev => prev + 1);
        setFeedback('retry');
      } else {
        learningSessionDispatch({
          type: 'ANSWER_TEST',
          answer: '',
        });
      }
    },
    [attempts, character, learningSessionDispatch],
  );

  const renderTracer = () => {
    if (characterType === 'hanzi') {
      return (
        <HanziWriterTracer
          character={character}
          difficulty={difficulty}
          onComplete={handleComplete}
          disabled={isDisabled}
        />
      );
    }

    return (
      <CanvasTracer
        character={character}
        difficulty={difficulty}
        onComplete={handleComplete}
        disabled={isDisabled}
      />
    );
  };

  return (
    <S.Container data-testid="tracing-response">
      <S.TracingArea>{renderTracer()}</S.TracingArea>
      {feedback === 'retry' && (
        <S.FeedbackMessage $isError>{intl.formatMessage(msgs.tryAgain)}</S.FeedbackMessage>
      )}
      {feedback === 'correct' && (
        <S.FeedbackMessage>{intl.formatMessage(msgs.correct)}</S.FeedbackMessage>
      )}
    </S.Container>
  );
};
